/* eslint-disable curly */
import { convertToExcalidrawElements } from "@excalidraw/excalidraw";
import { CaptureUpdateAction } from "@excalidraw/excalidraw";
import { randomId } from "@excalidraw/common";
import { useEffect } from "react";

import type { ExcalidrawImperativeAPI } from "@excalidraw/excalidraw/types";

const STICKY_SIZE = 220;
const STICKY_COLOR = "#fef3bd";
const STICKY_BORDER = "#d4a017";

// Figma-style default drop shadow: a real Gaussian shadow rendered by the
// canvas (see `customData.boxShadow` handling in packages/element renderElement).
// Centered (offsetX 0), sitting just below the note, soft blur, low opacity.
export const DEFAULT_STICKY_SHADOW = {
  color: "#0f172a",
  opacity: 0.28,
  blur: 16,
  offsetX: 0,
  offsetY: 8,
  enabled: true,
};

export const createStickyNote = (excalidrawAPI: ExcalidrawImperativeAPI) => {
  const state = excalidrawAPI.getAppState();

  const viewportCx = state.width / 2;
  const viewportCy = state.height / 2;
  const sceneCx = viewportCx / state.zoom.value - state.scrollX;
  const sceneCy = viewportCy / state.zoom.value - state.scrollY;
  const x = Math.round(sceneCx - STICKY_SIZE / 2);
  const y = Math.round(sceneCy - STICKY_SIZE / 2);

  const groupId = randomId();

  // A sticky note is now a SINGLE rectangle (+ bound text). The shadow is a
  // real drop shadow attached to the rect via customData, not a separate shape.
  const skeleton = [
    {
      type: "rectangle" as const,
      x,
      y,
      width: STICKY_SIZE,
      height: STICKY_SIZE,
      backgroundColor: STICKY_COLOR,
      strokeColor: STICKY_BORDER,
      fillStyle: "solid" as const,
      strokeWidth: 1,
      strokeStyle: "solid" as const,
      roughness: 0,
      opacity: 100,
      roundness: { type: 3 as const },
      groupIds: [groupId],
      customData: {
        stickyNote: true,
        boxShadow: { ...DEFAULT_STICKY_SHADOW },
      },
      label: {
        text: "",
        fontSize: 20,
        textAlign: "center" as const,
        verticalAlign: "middle" as const,
        groupIds: [groupId],
      },
    },
  ];

  const created = convertToExcalidrawElements(skeleton as any);

  // The bound text created by convertToExcalidrawElements may not inherit
  // groupIds from the label spec, so make every produced element share the
  // groupId (single-click selects + drags the note and its label together).
  const grouped = created.map((el) => {
    const groupIds = el.groupIds ?? [];
    return groupIds.includes(groupId)
      ? el
      : { ...el, groupIds: [...groupIds, groupId] };
  });

  const existing = excalidrawAPI.getSceneElementsIncludingDeleted();

  // Select the whole group so an immediate drag after creation moves the note
  // + label together. Bound text is selected via its container, so it's
  // excluded from selectedElementIds.
  const selectedElementIds: Record<string, true> = {};
  for (const el of grouped) {
    if (!("containerId" in el && el.containerId)) {
      selectedElementIds[el.id] = true;
    }
  }

  excalidrawAPI.updateScene({
    elements: [...existing, ...grouped],
    appState: {
      selectedElementIds,
      selectedGroupIds: { [groupId]: true },
      editingGroupId: null,
    },
    captureUpdate: CaptureUpdateAction.IMMEDIATELY,
  });
  excalidrawAPI.scrollToContent(grouped, { animate: true });
};

interface Props {
  excalidrawAPI: ExcalidrawImperativeAPI | null;
}

/**
 * Headless component: listens for the toolbar "stickyNote" button event and
 * the keyboard shortcut N. Creates a sticky note when triggered.
 */
export const StickyNoteToolHandler = ({ excalidrawAPI }: Props) => {
  useEffect(() => {
    if (!excalidrawAPI) return;

    const onCreate = () => createStickyNote(excalidrawAPI);

    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (
        target &&
        (target.tagName === "INPUT" ||
          target.tagName === "TEXTAREA" ||
          target.isContentEditable)
      ) {
        return;
      }
      if (e.key === "n" || e.key === "N") {
        if (e.ctrlKey || e.metaKey || e.altKey) return;
        e.preventDefault();
        createStickyNote(excalidrawAPI);
      }
    };

    window.addEventListener("excalidraw:create-sticky-note", onCreate);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("excalidraw:create-sticky-note", onCreate);
      window.removeEventListener("keydown", onKey);
    };
  }, [excalidrawAPI]);

  return null;
};
